/**
 * Bulk-import preview → the groups the source's variant grouping rule
 * proposes. One card per group: Accept / Reject, pick the parent, split a
 * member off. Nothing is grouped until a card is accepted.
 */
import { useState } from 'react';
import { Button } from '../common/Button';
import {
  effectiveMembers,
  effectiveParent,
  type RuleGroup,
  type RuleGroupDecision,
} from './variantGroupingRule';
import type { VariantGroupingRule } from '../../types';

interface Props {
  rule: VariantGroupingRule;
  groups: RuleGroup[];
  decisions: ReadonlyMap<string, RuleGroupDecision>;
  onDecide: (key: string, decision: RuleGroupDecision) => void;
  /** Bulk set every group's status (keeps picks and splits). */
  onDecideAll: (status: RuleGroupDecision['status']) => void;
  refOf: (index: number) => string;
  /** Secondary text for a row (model / name). */
  labelOf: (index: number) => string;
}

const RULE_LABEL: Record<VariantGroupingRule['type'], string> = {
  none: 'none',
  stem: 'first N characters',
  splitter: 'before the splitter',
  regex: 'pattern',
};

const STATUS_TONE: Record<RuleGroupDecision['status'], string> = {
  proposed: 'border-gray-200',
  accepted: 'border-green-300 bg-green-50/40',
  rejected: 'border-gray-200 opacity-60',
};

export function VariantRuleGroupsPanel({ rule, groups, decisions, onDecide, onDecideAll, refOf, labelOf }: Props) {
  const [open, setOpen] = useState(true);
  if (groups.length === 0) return null;
  const accepted = groups.filter((g) => decisions.get(g.key)?.status === 'accepted').length;

  return (
    <div className="rounded border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="text-sm font-medium text-indigo-900"
        >
          {open ? '▾' : '▸'} Variant grouping rule ({RULE_LABEL[rule.type]}
          {rule.value ? <span className="font-mono"> {rule.value}</span> : null}): {groups.length} proposed group
          {groups.length === 1 ? '' : 's'} · {accepted} accepted
        </button>
        <span className="flex-1" />
        <Button size="sm" variant="secondary" onClick={() => onDecideAll('accepted')}>
          Accept all
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDecideAll('rejected')}>
          Reject all
        </Button>
      </div>
      {open && (
        <>
          <p className="text-xs text-gray-600">
            Accepted groups import their parent first, as an ordinary reference, and every other member as its variant.
            Proposed and rejected groups import exactly as before.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            {groups.map((g) => {
              const d = decisions.get(g.key) ?? { status: 'proposed' as const };
              const members = effectiveMembers(g, d);
              const parent = effectiveParent(g, d, refOf);
              const split = new Set(d.splitOff ?? []);
              const tooSmall = members.length < 2;
              return (
                <div key={g.key} data-testid={`rule-group-${g.key}`} className={`rounded border bg-white p-2 ${STATUS_TONE[d.status]}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-gray-900">{g.key}</span>
                    <span className="text-xs text-gray-500">
                      {members.length} member{members.length === 1 ? '' : 's'}
                      {split.size > 0 ? ` · ${split.size} split off` : ''}
                    </span>
                    <span className="flex-1" />
                    <Button
                      size="sm"
                      variant={d.status === 'accepted' ? 'primary' : 'secondary'}
                      disabled={tooSmall}
                      onClick={() => onDecide(g.key, { ...d, status: d.status === 'accepted' ? 'proposed' : 'accepted' })}
                    >
                      {d.status === 'accepted' ? 'Accepted ✓' : 'Accept'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDecide(g.key, { ...d, status: d.status === 'rejected' ? 'proposed' : 'rejected' })}
                    >
                      {d.status === 'rejected' ? 'Undo reject' : 'Reject'}
                    </Button>
                  </div>
                  {tooSmall && <p className="text-[11px] text-amber-700">Fewer than two members left — this group won’t be written.</p>}
                  <ul className="mt-1 space-y-0.5">
                    {g.members.map((i) => {
                      const isSplit = split.has(i);
                      return (
                        <li key={i} className={`flex items-center gap-2 text-xs ${isSplit ? 'text-gray-400 line-through' : ''}`}>
                          <input
                            type="radio"
                            name={`rule-parent-${g.key}`}
                            aria-label={`Parent: ${refOf(i)}`}
                            checked={!isSplit && parent === i}
                            disabled={isSplit || d.status === 'rejected'}
                            onChange={() => onDecide(g.key, { ...d, parentIndex: i })}
                          />
                          <span className="font-mono text-gray-900">{refOf(i)}</span>
                          <span className="truncate text-gray-500">{labelOf(i)}</span>
                          {!isSplit && parent === i && <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-800">parent</span>}
                          <span className="flex-1" />
                          <button
                            type="button"
                            disabled={d.status === 'rejected'}
                            onClick={() =>
                              onDecide(g.key, {
                                ...d,
                                splitOff: isSplit ? (d.splitOff ?? []).filter((x) => x !== i) : [...(d.splitOff ?? []), i],
                              })
                            }
                            className="text-[11px] text-indigo-700 hover:underline disabled:opacity-40"
                          >
                            {isSplit ? 'Rejoin' : 'Split off'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
